import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { createContext, useCallback, useRef, useState, useEffect, useMemo, useContext } from "react";
import { Filters, FiltersProvider, Operators, HorizontalChips, FilterDrawerToggle } from "@livefree/filters";
import { useLocalStorage } from "@livefree/react";
import { xor, pascalCase } from "@livefree/common";
import "dexie";
import { Dropdown, Button, Label, Tooltip, Separator, Spinner, Pagination, ButtonGroup, Table } from "@heroui/react";
import { RiRefreshLine, RiAddLine, RiInfinityFill } from "@remixicon/react";
import { DataGrid } from "@heroui-pro/react";
import { LoadingBar } from "@livefree/react-ui";
const RequestBrokerContext = createContext(null);
class MetadataKeys {
  static paging = (actionKey) => `livefree.request-broker.paging.${actionKey}`;
  static sorting = (actionKey) => `livefree.request-broker.sorting.${actionKey}`;
  static filters = (actionKey) => `livefree.request-broker.filters.${actionKey}`;
}
class Paging {
  #take;
  #page;
  #count;
  handlers;
  constructor(size) {
    this.#take = size ?? 50;
    this.#page = 1;
    this.#count = 0;
    this.handlers = [];
  }
  static deserialize(val) {
    const obj = JSON.parse(val);
    const paging = new Paging(obj.take);
    paging.count = obj.count;
    paging.page = obj.page;
    return paging;
  }
  get take() {
    return this.#take;
  }
  set take(size) {
    this.#take = Math.max(1, size);
    this.resetPage();
    this.handlers.forEach((handler) => handler(this));
  }
  get page() {
    return this.#page;
  }
  set page(num) {
    this.#page = Math.max(1, this.pages > 0 ? Math.min(num, this.pages) : num);
    this.handlers.forEach((handler) => handler(this));
  }
  get count() {
    return this.#count;
  }
  set count(total) {
    this.#count = Math.max(0, total);
    this.resetPage();
  }
  get pages() {
    return Math.ceil(this.#count / this.#take);
  }
  get skip() {
    return Math.max(0, this.#take * (this.#page - 1));
  }
  subscribe(handler) {
    if (!this.handlers.includes(handler)) {
      this.handlers.push(handler);
    }
  }
  unsubscribe(handler) {
    if (this.handlers.includes(handler)) {
      this.handlers = this.handlers.slice(0, this.handlers.indexOf(handler)).concat(this.handlers.slice(this.handlers.indexOf(handler) + 1));
    }
  }
  serialize() {
    return JSON.stringify({
      page: this.page,
      take: this.take,
      count: this.count
    });
  }
  serializable() {
    return {
      take: this.take,
      page: this.page,
      count: this.count
    };
  }
  resetPage() {
    if (this.pages > 0 && this.page > this.pages) {
      this.#page = 1;
    }
  }
}
class Sorting {
  #sortBy;
  #type;
  fields;
  handlers;
  constructor(sortable, sortBy, type) {
    this.fields = sortable ?? [];
    this.#sortBy = sortBy ?? "";
    this.#type = type ?? "asc";
    this.handlers = [];
  }
  static deserialize(val) {
    const obj = JSON.parse(val);
    return new Sorting(obj.fields ?? [], obj.sortBy, obj.type);
  }
  get sortBy() {
    return this.#sortBy;
  }
  set sortBy(sort) {
    this.#sortBy = sort;
    this.handlers.forEach((handler) => handler(this));
  }
  get type() {
    return this.#type;
  }
  set type(sortType) {
    this.#type = sortType;
    this.handlers.forEach((handler) => handler(this));
  }
  subscribe(handler) {
    if (!this.handlers.includes(handler)) {
      this.handlers.push(handler);
    }
  }
  unsubscribe(handler) {
    if (this.handlers.includes(handler)) {
      this.handlers = this.handlers.slice(0, this.handlers.indexOf(handler)).concat(this.handlers.slice(this.handlers.indexOf(handler) + 1));
    }
  }
  serialize() {
    return JSON.stringify({
      fields: this.fields,
      sortBy: this.sortBy,
      type: this.type
    });
  }
  serializable() {
    return {
      sortBy: this.sortBy,
      type: this.type,
      fields: this.fields
    };
  }
}
function useFiltersSync(key, initialValue) {
  const appliedFilters = initialValue.getActive();
  const [value, setValue] = useLocalStorage(key, appliedFilters);
  const deserialized = initialValue.deserialize(value);
  const handleUpdate = useCallback(
    (val) => {
      setValue(() => val.getActive());
    },
    [setValue]
  );
  return [deserialized, handleUpdate];
}
function useDependencyMonitor(deps, onDependenciesChanged) {
  const isInitial = useRef(true);
  const [updated, setUpdated] = useState(false);
  const handleExecute = useCallback(() => {
    if (updated) {
      onDependenciesChanged();
      setUpdated(false);
    }
  }, [onDependenciesChanged, updated]);
  const dependency = JSON.stringify(deps ?? "");
  useEffect(() => {
    const deps2 = JSON.parse(dependency);
    if (!deps2 || Array.isArray(deps2) && deps2.length === 0) {
      return;
    }
    if (isInitial.current) {
      isInitial.current = false;
      return;
    }
    setUpdated(true);
  }, [dependency]);
  if (updated) {
    handleExecute();
  }
}
function usePagingSync(key, initialValue) {
  const [value, setValue] = useLocalStorage(key, initialValue.serializable());
  const deserialized = new Paging();
  deserialized.count = value.count;
  deserialized.take = value.take;
  deserialized.page = value.page;
  const handleUpdate = useCallback(
    (val) => {
      setValue(() => val.serializable());
    },
    [setValue]
  );
  return [deserialized, handleUpdate];
}
function useInitialPaging(actionKey, paging) {
  const defaultPaging = useMemo(() => {
    return paging instanceof Paging ? paging : typeof paging === "object" ? new Paging(paging.take) : new Paging(50);
  }, [paging]);
  return usePagingSync(MetadataKeys.paging(actionKey), defaultPaging);
}
function useSortingSync(key, initialValue) {
  const [value, setValue] = useLocalStorage(key, initialValue.serializable());
  const deserialized = new Sorting(value.fields);
  deserialized.sortBy = value.sortBy;
  deserialized.type = value.type;
  const handleUpdate = useCallback(
    (val) => {
      setValue(() => val.serializable());
    },
    [setValue]
  );
  return [deserialized, handleUpdate];
}
function useInitialSorting(actionKey, sorting) {
  const defaultSorting = useMemo(() => {
    return sorting instanceof Sorting ? sorting : typeof sorting === "object" ? new Sorting(sorting.fields, sorting.sortBy, sorting.type) : new Sorting([]);
  }, [sorting]);
  const [sort, setSort] = useSortingSync(MetadataKeys.sorting(actionKey), defaultSorting);
  useEffect(() => {
    const diff = xor(
      sort.fields.map((x) => x.value),
      defaultSorting.fields.map((x) => x.value)
    );
    if (diff.length > 0) {
      setSort(defaultSorting);
    }
  }, [sort, defaultSorting, setSort]);
  return [sort, setSort];
}
function useExecuteHandler(onExecute, action, paging, sorting, filters) {
  return useCallback(
    (params) => {
      onExecute(action, {
        paging: params?.paging ?? paging,
        sorting: params?.sorting ?? sorting,
        filters: params?.filters ?? filters
      });
    },
    [onExecute, action, paging, sorting, filters]
  );
}
function useInitialExecution(skipInitial, handleOnExecute) {
  const shouldPerformInitialExecute = useRef(true);
  useEffect(() => {
    (async () => {
      let skip = false;
      if (skipInitial) {
        skip = await skipInitial();
      }
      if (!skip && shouldPerformInitialExecute.current) {
        shouldPerformInitialExecute.current = false;
        handleOnExecute();
      }
    })();
  }, [skipInitial, handleOnExecute]);
}
function useFilterHandler(execute, setFilters) {
  return useCallback(
    (filters) => {
      setFilters(filters);
      execute({
        filters
      });
    },
    [execute, setFilters]
  );
}
const useStaticFilters = (filters, staticFilters) => {
  const handlerRef = useRef(staticFilters);
  handlerRef.current = staticFilters;
  const dependency = JSON.stringify(staticFilters?.deps ?? []);
  useEffect(() => {
    handlerRef.current?.handler?.(filters);
  }, [filters, dependency]);
};
function RequestBroker(props) {
  const {
    children,
    actionKey,
    isExecuting,
    onExecute,
    action,
    actions,
    skipInitial,
    staticFilters,
    deps
  } = props;
  const [paging, setPaging] = useInitialPaging(actionKey, props.paging ?? new Paging());
  const [sorting, setSorting] = useInitialSorting(actionKey, props.sorting ?? new Sorting([]));
  const [filters, setFilters] = useFiltersSync(
    MetadataKeys.filters(actionKey),
    props.filters ?? new Filters([])
  );
  const handleOnExecute = useExecuteHandler(onExecute, action, paging, sorting, filters);
  const filterHandler = useFilterHandler(handleOnExecute, setFilters);
  useStaticFilters(filters, staticFilters);
  useInitialExecution(skipInitial, handleOnExecute);
  useDependencyMonitor(deps, handleOnExecute);
  const context = useMemo(
    () => ({
      key: actionKey,
      setPaging: (paging2) => {
        setPaging(paging2);
        handleOnExecute({ paging: paging2 });
      },
      setSorting: (sorting2) => {
        setSorting(sorting2);
        handleOnExecute({ sorting: sorting2 });
      },
      execute: handleOnExecute,
      isExecuting,
      actions: actions?.reduce((prev, next) => {
        prev[next.name] = next.execute({ paging, sorting, filters });
        return prev;
      }, {}),
      metadata: { paging, sorting, filters }
    }),
    [
      actionKey,
      paging,
      sorting,
      filters,
      setPaging,
      setSorting,
      isExecuting,
      handleOnExecute,
      actions
    ]
  );
  return /* @__PURE__ */ jsx(FiltersProvider, { filters, onApply: filterHandler, children: /* @__PURE__ */ jsx(RequestBrokerContext.Provider, { value: context, children }) });
}
class RequestMetadata {
  static get(actionKey) {
    let paging;
    let sorting;
    let filters;
    const serializedPaging = localStorage.getItem(MetadataKeys.paging(actionKey));
    if (serializedPaging) {
      paging = Paging.deserialize(serializedPaging);
    }
    const serializedSorting = localStorage.getItem(MetadataKeys.sorting(actionKey));
    if (serializedSorting) {
      sorting = Sorting.deserialize(serializedSorting);
    }
    const serializedFilters = localStorage.getItem(MetadataKeys.filters(actionKey));
    if (serializedFilters) {
      const obj = JSON.parse(serializedFilters);
      filters = obj.map((filter) => ({
        name: filter.name,
        searchType: filter.searchType,
        value: filter.value
      }));
    }
    return { paging, sorting, filters };
  }
  static setCount(actionKey, count) {
    const serialized = localStorage.getItem(MetadataKeys.paging(actionKey));
    if (serialized) {
      const paging = JSON.parse(serialized);
      paging.count = count;
      localStorage.setItem(MetadataKeys.paging(actionKey), JSON.stringify(paging));
    }
  }
}
const useRequestBroker = () => {
  const context = useContext(RequestBrokerContext);
  if (!context) {
    throw new Error(
      "RequestBrokerContext not initialized. Did you forget to wrap you component with a RequestBroker?"
    );
  }
  return context;
};
const useFilters = () => {
  const broker = useRequestBroker();
  return broker.metadata.filters;
};
const usePaging = () => {
  const broker = useRequestBroker();
  return [broker.metadata.paging, broker.setPaging, broker];
};
const useSorting = () => {
  const broker = useRequestBroker();
  return [broker.metadata.sorting, broker.setSorting, broker];
};
function dexieRequestBrokerAddon(db) {
  if (!Object.hasOwn(db, "withRequestBroker")) {
    Object.defineProperty(db, "withRequestBroker", {
      configurable: false,
      enumerable: false,
      value: (actionKey, execute, options) => {
        const sorting = options?.sorting ?? true;
        const paging = options?.paging ?? false;
        const metadata = RequestMetadata.get(actionKey);
        const query = execute(db);
        let collection = query;
        if (query instanceof db.Table) {
          collection = query.toCollection();
        }
        if (metadata) {
          if (sorting && metadata.sorting && metadata.sorting.sortBy) {
            if (metadata.sorting.type == "desc") {
              collection = collection.reverse();
            }
            collection = collection.sortBy(
              ((x) => x.charAt(0).toLowerCase() + x.slice(1))(metadata.sorting.sortBy)
            );
          }
          if (paging && metadata.paging) {
            if (collection instanceof db.Collection) {
              collection = collection.toArray();
            }
            const { skip, take } = metadata.paging;
            return collection.then((x) => {
              return x.slice(skip, skip + take);
            });
          }
          return collection instanceof db.Collection ? collection.toArray() : collection;
        }
        return collection.toArray();
      }
    });
  }
}
class MqlRequestBuilder {
  metadata;
  options;
  constructor(metadata, options = {}) {
    this.metadata = metadata;
    this.options = { withCount: true, ...options };
  }
  static create(metadata, options = {}) {
    return new MqlRequestBuilder(metadata, options);
  }
  buildPaging() {
    return pagerToMql(this.metadata.paging, this.options.withCount);
  }
  buildSorting() {
    return sortingToMql(this.metadata.sorting);
  }
  buildFilters() {
    const filters = this.metadata.filters;
    if (filters) {
      const mql = Filter.initialize(filters).toMql();
      return mql ? `MATCH[${mql}]` : "";
    }
    return "";
  }
  build(model) {
    return this.buildRequest(`FETCH ${model}`);
  }
  buildRequest(fetch) {
    if (this.metadata) {
      return `${fetch} ${this.buildFilters()} ${this.buildSorting()} ${this.buildPaging()};`;
    }
    return `${fetch};`;
  }
}
class Filter {
  filters;
  constructor(filters) {
    this.filters = filters;
  }
  static initialize = (filters) => new Filter(filters);
  toMql = () => {
    const filterMql = this.filters.getActive().map((filter) => {
      let mql = "";
      switch (filter.searchType) {
        case "in":
          mql = `${this.#formatValue({
            fieldName: filter.name,
            searchType: filter.searchType,
            filterValue: filter.value
          })}`;
          break;
        default:
          mql = `${pascalCase(filter.name)} ${Operators[filter.searchType]} ${this.#formatValue({
            searchType: filter.searchType,
            filterValue: filter.value
          })}`;
          break;
      }
      return mql;
    });
    return filterMql?.length > 0 ? filterMql.filter((x) => x !== "").join(" AND ") : "";
  };
  #formatValue = ({
    fieldName,
    searchType,
    filterValue
  }) => {
    let format = void 0;
    switch (searchType) {
      case "between":
        if (!Array.isArray(filterValue)) throw new Error("The value of filter must be an array");
        else {
          const range = {
            start: Number.isFinite(filterValue[0]) ? filterValue[0] : `'${filterValue[0]}'`,
            end: Number.isFinite(filterValue[1]) ? filterValue[1] : `'${filterValue[1]}'`
          };
          format = `${range.start} +&+ ${range.end}`;
        }
        break;
      case "in":
        if (!Array.isArray(filterValue)) throw new Error("The value of filter must be an array");
        if (filterValue.length > 1) {
          format = "(";
          filterValue.map((item) => {
            format += `${fieldName} ${Operators["equals"]} ${Number.isFinite(item) ? item : `'${item}'`} ${filterValue[filterValue.length - 1] !== item ? " OR " : ""}`;
          });
          format += ")";
        } else if (filterValue.length === 1) {
          format = `${fieldName} ${Operators["equals"]} ${Number.isFinite(filterValue[0]) ? filterValue[0] : `'${filterValue[0]}'`}`;
        } else format = "";
        break;
      default:
        format = Number.isFinite(filterValue) ? filterValue?.toString() : `'${filterValue}'`;
        break;
    }
    return format;
  };
}
function pagerToMql(pager, withCount = true) {
  if (pager && pager.take && pager.take > 0) {
    const skip = isNaN(pager.skip) ? 0 : pager.skip;
    const take = isNaN(pager.take) ? 50 : pager.take;
    return `SKIP ${skip} TAKE ${take}${withCount ? " WITH COUNT" : ""}`;
  }
  return "";
}
function sortingToMql(sorting) {
  if (sorting) {
    if (sorting.sortBy) {
      return `SORTBY ${pascalCase(sorting.sortBy)}${sorting.type === "desc" ? " DESC" : ""}`;
    }
  }
  return "";
}
function SortDropdown() {
  const [sorting, setSorting, broker] = useSorting();
  return /* @__PURE__ */ jsxs(Dropdown, { children: [
    /* @__PURE__ */ jsx(
      Button,
      {
        size: "sm",
        isIconOnly: true,
        className: "rounded-full",
        variant: "primary",
        isDisabled: sorting.fields.length === 0 || broker.isExecuting,
        children: /* @__PURE__ */ jsx("i", { className: sorting.type === "asc" ? "ri-sort-asc" : "ri-sort-desc" })
      }
    ),
    /* @__PURE__ */ jsx(Dropdown.Popover, { children: /* @__PURE__ */ jsxs(Dropdown.Menu, { "aria-label": "Sorting", children: [
      /* @__PURE__ */ jsx(Dropdown.Section, { children: sorting.fields.map((field) => /* @__PURE__ */ jsxs(Dropdown.Item, { id: field.title, textValue: field.title, className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsx(Label, { children: field.title }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-center gap-2", children: [
          /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                size: "sm",
                variant: field.value === sorting.sortBy && sorting.type === "asc" ? "primary" : "tertiary",
                className: "rounded-full",
                isIconOnly: true,
                isDisabled: field.value === sorting.sortBy && sorting.type === "asc",
                onPress: () => {
                  sorting.sortBy = field.value;
                  sorting.type = "asc";
                  setSorting(sorting);
                },
                children: /* @__PURE__ */ jsx("i", { className: "ri-sort-asc" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Sort Ascending" })
          ] }),
          /* @__PURE__ */ jsxs(Tooltip, { delay: 0, children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                size: "sm",
                variant: field.value === sorting.sortBy && sorting.type === "desc" ? "primary" : "tertiary",
                className: "rounded-full",
                isIconOnly: true,
                isDisabled: field.value === sorting.sortBy && sorting.type === "desc",
                onPress: () => {
                  sorting.sortBy = field.value;
                  sorting.type = "desc";
                  setSorting(sorting);
                },
                children: /* @__PURE__ */ jsx("i", { className: "ri-sort-desc" })
              }
            ),
            /* @__PURE__ */ jsx(Tooltip.Content, { children: "Sort Descending" })
          ] })
        ] })
      ] }, field.title)) }),
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsx(Dropdown.Section, { children: /* @__PURE__ */ jsx(
        Dropdown.Item,
        {
          id: "clearAll",
          textValue: "Clear Sort",
          onPress: () => {
            sorting.sortBy = "";
            sorting.type = "asc";
            setSorting(sorting);
          },
          className: "text-center",
          children: /* @__PURE__ */ jsx(Label, { children: "Clear Sort" })
        }
      ) })
    ] }) })
  ] });
}
function ReloadButton() {
  const broker = useRequestBroker();
  return /* @__PURE__ */ jsx(
    Button,
    {
      size: "sm",
      variant: "primary",
      isPending: broker.isExecuting,
      onPress: () => broker.execute(),
      children: ({ isPending }) => /* @__PURE__ */ jsxs(Fragment, { children: [
        isPending ? /* @__PURE__ */ jsx(Spinner, { color: "current", size: "sm" }) : /* @__PURE__ */ jsx(RiRefreshLine, { size: "16" }),
        /* @__PURE__ */ jsx("span", { className: "relative top-px", children: isPending ? "Loading..." : "Reload" })
      ] })
    }
  );
}
function HorizontalControl({
  children,
  placement,
  hideChips,
  hideSort,
  hideFilters,
  hideReload,
  className,
  chipScope,
  onCreate,
  createLabel = "Create",
  createEnabled = true
}) {
  const broker = useRequestBroker();
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-row ${className ?? ""}`, children: [
    /* @__PURE__ */ jsx("div", { children: !(hideChips ?? false) && /* @__PURE__ */ jsx(HorizontalChips, { scope: chipScope }) }),
    /* @__PURE__ */ jsx("div", { className: "flex-grow", children }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-row items-center justify-end gap-1", children: [
      !(hideReload ?? false) && /* @__PURE__ */ jsx(ReloadButton, {}),
      !(hideSort ?? false) && /* @__PURE__ */ jsx(SortDropdown, {}),
      !(hideFilters ?? false) && /* @__PURE__ */ jsx(FilterDrawerToggle, { placement: placement ?? "right", isDisabled: broker.isExecuting }),
      onCreate && /* @__PURE__ */ jsxs(Button, { size: "sm", variant: "secondary", className: "text-success", onPress: onCreate, isDisabled: !createEnabled, children: [
        /* @__PURE__ */ jsx(RiAddLine, { size: "16" }),
        /* @__PURE__ */ jsx("span", { className: "relative top-px", children: createLabel })
      ] })
    ] })
  ] });
}
function r(e) {
  var t, f, n = "";
  if ("string" == typeof e || "number" == typeof e) n += e;
  else if ("object" == typeof e) if (Array.isArray(e)) {
    var o = e.length;
    for (t = 0; t < o; t++) e[t] && (f = r(e[t])) && (n && (n += " "), n += f);
  } else for (f in e) e[f] && (n && (n += " "), n += f);
  return n;
}
function clsx() {
  for (var e, t, f = 0, n = "", o = arguments.length; f < o; f++) (e = arguments[f]) && (t = r(e)) && (n && (n += " "), n += t);
  return n;
}
const buildPages = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push("start-ellipsis");
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) {
    pages.push(p);
  }
  if (current < total - 2) pages.push("end-ellipsis");
  pages.push(total);
  return pages;
};
function Pager(props) {
  const [paging, setPaging, broker] = usePaging();
  const align = props.align ?? "center";
  const variant = props.variant ?? "pages";
  const classes = clsx({
    "flex flex-row": true,
    "items-center": true,
    "w-fit gap-2 py-2.5": true,
    "justify-start": align === "left",
    "justify-center": align === "center",
    "justify-end": align === "right"
  });
  const goTo = (page) => {
    paging.page = page;
    setPaging(paging);
  };
  if (variant === "simple") {
    const isLast = paging.pages > 0 && paging.page >= paging.pages;
    return /* @__PURE__ */ jsx("div", { className: classes, children: /* @__PURE__ */ jsx(Pagination, { className: props.className, children: /* @__PURE__ */ jsxs(Pagination.Content, { children: [
      /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(
        Pagination.Previous,
        {
          className: "cursor-pointer",
          isDisabled: broker.isExecuting || paging.page <= 1,
          onPress: () => goTo(paging.page - 1),
          children: /* @__PURE__ */ jsx(Pagination.PreviousIcon, {})
        }
      ) }),
      /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(Pagination.Link, { className: "cursor-default", isActive: true, children: paging.page }) }),
      /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(
        Pagination.Next,
        {
          className: "cursor-pointer",
          isDisabled: broker.isExecuting || isLast,
          onPress: () => goTo(paging.page + 1),
          children: /* @__PURE__ */ jsx(Pagination.NextIcon, {})
        }
      ) })
    ] }) }) });
  }
  return paging.pages > 0 && /* @__PURE__ */ jsx("div", { className: classes, children: /* @__PURE__ */ jsx(Pagination, { className: props.className, children: /* @__PURE__ */ jsxs(Pagination.Content, { children: [
    /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(
      Pagination.Previous,
      {
        className: "cursor-pointer",
        isDisabled: broker.isExecuting,
        onPress: () => goTo(paging.page > 1 ? paging.page - 1 : paging.pages),
        children: /* @__PURE__ */ jsx(Pagination.PreviousIcon, {})
      }
    ) }),
    buildPages(paging.page, paging.pages).map(
      (p) => typeof p === "number" ? /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(
        Pagination.Link,
        {
          className: "cursor-pointer",
          isActive: paging.page === p,
          isDisabled: broker.isExecuting,
          onPress: () => goTo(p),
          children: p
        }
      ) }, p) : /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(Pagination.Ellipsis, {}) }, p)
    ),
    /* @__PURE__ */ jsx(Pagination.Item, { children: /* @__PURE__ */ jsx(
      Pagination.Next,
      {
        className: "cursor-pointer",
        isDisabled: broker.isExecuting,
        onPress: () => goTo(paging.page < paging.pages ? paging.page + 1 : 1),
        children: /* @__PURE__ */ jsx(Pagination.NextIcon, {})
      }
    ) })
  ] }) }) });
}
function PageSizeInput({
  allowInfinity = false,
  ...props
}) {
  const [paging, setPaging] = usePaging();
  const sizes = [10, 25, 50, 100, 500];
  return /* @__PURE__ */ jsxs(ButtonGroup, { size: "sm", variant: "tertiary", ...props, children: [
    sizes.map((size) => /* @__PURE__ */ jsx(
      Button,
      {
        size: "sm",
        isDisabled: paging.take === size,
        onPress: () => {
          paging.take = size;
          setPaging(paging);
        },
        children: size
      },
      size
    )),
    allowInfinity && /* @__PURE__ */ jsx(
      Button,
      {
        size: "sm",
        isIconOnly: true,
        isDisabled: paging.take === 1e5,
        onPress: () => {
          paging.take = 1e5;
          setPaging(paging);
        },
        children: /* @__PURE__ */ jsx(RiInfinityFill, { size: 16 })
      }
    )
  ] });
}
function HorizontalPager({
  children,
  hidePageSize,
  className,
  pagerStyle = "pages"
}) {
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-row items-center ${className ?? ""}`, children: [
    /* @__PURE__ */ jsx(Pager, { variant: pagerStyle }),
    /* @__PURE__ */ jsx("div", { className: "flex-grow", children }),
    !(hidePageSize ?? false) && /* @__PURE__ */ jsx(PageSizeInput, { size: "sm" })
  ] });
}
function RequestBrokerContainer({
  children,
  placement,
  hideChips,
  hideSort,
  hideFilters,
  hideReload,
  chipScope,
  hidePageSize,
  topContent,
  bottomContent,
  classNames
}) {
  return /* @__PURE__ */ jsxs("div", { className: `flex flex-col flex-1 h-0 ${classNames?.base ?? ""}`, children: [
    /* @__PURE__ */ jsx(
      HorizontalControl,
      {
        placement,
        hideChips,
        hideSort,
        hideFilters,
        hideReload,
        chipScope,
        className: `pb-1 ${classNames?.header ?? ""}`,
        children: topContent
      }
    ),
    /* @__PURE__ */ jsx("div", { className: "flex-1 h-0 overflow-y-auto", children }),
    /* @__PURE__ */ jsx(HorizontalPager, { hidePageSize, className: `mt-1 ${classNames?.footer ?? ""}`, children: bottomContent })
  ] });
}
function RequestBrokerDataGrid({
  placement,
  hideChips,
  hideFilters,
  hideReload,
  chipScope,
  hidePageSize,
  pagerStyle,
  topContent,
  bottomContent,
  onCreate,
  createLabel,
  createEnabled = true,
  classNames,
  className,
  scrollContainerClassName,
  sortDescriptor,
  onSortChange,
  data,
  contentClassName,
  onRowAction,
  "aria-label": ariaLabel = "Results",
  ...rest
}) {
  const broker = useRequestBroker();
  const { sorting } = broker.metadata;
  const isBusy = broker.isExecuting;
  const clickableRowClass = onRowAction ? "[&_[role=row]]:cursor-pointer" : "";
  const brokerSortDescriptor = sorting.sortBy ? {
    column: sorting.sortBy,
    direction: sorting.type === "asc" ? "ascending" : "descending"
  } : void 0;
  const handleSortChange = (descriptor) => {
    sorting.sortBy = String(descriptor.column);
    sorting.type = descriptor.direction === "descending" ? "desc" : "asc";
    broker.setSorting(sorting);
  };
  return /* @__PURE__ */ jsxs("div", { className: "flex-1 h-0 flex flex-col pb-2 gap-0", children: [
    /* @__PURE__ */ jsxs("div", { className: "relative bg-surface p-4 rounded-t-3xl shrink-0", children: [
      /* @__PURE__ */ jsx(
        HorizontalControl,
        {
          placement,
          hideChips,
          hideSort: true,
          hideFilters,
          hideReload,
          chipScope,
          onCreate,
          createLabel,
          createEnabled,
          className: classNames?.header ?? "",
          children: topContent
        }
      ),
      /* @__PURE__ */ jsx(LoadingBar, { isLoading: isBusy, placement: "bottom", label: "Loading results" })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "relative flex-1 h-0 flex flex-col", "aria-busy": broker.isExecuting, children: /* @__PURE__ */ jsx(
      DataGrid,
      {
        "aria-label": ariaLabel,
        data,
        className: clsx("h-full bg-surface shadow-none rounded-none px-0", className),
        contentClassName: clsx(clickableRowClass, contentClassName),
        scrollContainerClassName: clsx("h-full pt-0", scrollContainerClassName),
        sortDescriptor: sortDescriptor ?? brokerSortDescriptor,
        onSortChange: onSortChange ?? handleSortChange,
        onRowAction,
        ...rest
      }
    ) }),
    /* @__PURE__ */ jsx("div", { className: "bg-surface p-2 px-4 rounded-b-3xl shrink-0", children: /* @__PURE__ */ jsx(
      HorizontalPager,
      {
        hidePageSize,
        pagerStyle,
        className: classNames?.footer ?? "",
        children: bottomContent
      }
    ) })
  ] });
}
function RequestBrokerTable({
  placement,
  hideChips,
  hideSort,
  hideFilters,
  hideReload,
  chipScope,
  hidePageSize,
  topContent,
  bottomContent,
  classNames,
  items,
  renderRow,
  columns,
  renderColumn,
  ...rest
}) {
  const broker = useRequestBroker();
  return /* @__PURE__ */ jsxs("div", { className: "flex-1 h-0 flex flex-col pb-2 gap-0", children: [
    /* @__PURE__ */ jsx("div", { className: "bg-surface p-4 rounded-t-3xl shrink-0", children: /* @__PURE__ */ jsx(
      HorizontalControl,
      {
        placement,
        hideChips,
        hideSort,
        hideFilters,
        hideReload,
        chipScope,
        className: classNames?.header ?? "",
        children: topContent
      }
    ) }),
    /* @__PURE__ */ jsxs("div", { className: "relative flex-1 h-0 flex flex-col", children: [
      /* @__PURE__ */ jsx(Table, { className: "h-full bg-surface shadow-none rounded-none", children: /* @__PURE__ */ jsx(Table.ScrollContainer, { className: "h-full pt-0 scr", children: /* @__PURE__ */ jsxs(Table.Content, { "aria-label": "Results", ...rest, children: [
        /* @__PURE__ */ jsx(Table.Header, { className: "sticky top-0 z-10", columns, children: renderColumn }),
        /* @__PURE__ */ jsx(Table.Body, { items, children: renderRow })
      ] }) }) }),
      broker.isExecuting && /* @__PURE__ */ jsx("div", { className: "absolute inset-0 flex items-center justify-center backdrop-blur-sm z-50", children: /* @__PURE__ */ jsx(Spinner, {}) })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "bg-surface p-2 px-4 rounded-b-3xl shrink-0", children: /* @__PURE__ */ jsx(HorizontalPager, { hidePageSize, className: classNames?.footer ?? "", children: bottomContent }) })
  ] });
}
export {
  HorizontalControl,
  HorizontalPager,
  MetadataKeys,
  MqlRequestBuilder,
  PageSizeInput,
  Pager,
  Paging,
  ReloadButton,
  RequestBroker,
  RequestBrokerContainer,
  RequestBrokerContext,
  RequestBrokerDataGrid,
  RequestBrokerTable,
  RequestMetadata,
  SortDropdown,
  Sorting,
  dexieRequestBrokerAddon,
  useFilters,
  usePaging,
  useRequestBroker,
  useSorting
};
//# sourceMappingURL=index.js.map
